import { Globe, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';

import ToggleButton from '@/components/buttons/toggle-button/ToggleButton';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

import { S3File } from '../Home';

interface IPrivacyDialog {
  open: boolean;
  onClose: () => void;
  onSave: (key: string, enabled: boolean, cascade: boolean) => void;
  file: (S3File & { isPublic?: boolean }) | null;
}

export default function PrivacyDialog({ open, onClose, onSave, file }: IPrivacyDialog) {
  const [enabled, setEnabled] = useState(!!file?.isPublic);
  const [cascade, setCascade] = useState(false);

  useEffect(() => {
    setEnabled(!!file?.isPublic);
    setCascade(false);
  }, [file?.isPublic, file?.key]);

  const handleSave = async () => {
    onSave(file?.key || '', enabled, cascade);
    onClose();
  };

  if (!file) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>Chỉnh sửa quyền riêng tư</DialogTitle>
        </DialogHeader>

        <DialogDescription className="s3-explr-dialog-description break-all whitespace-normal">
          Tài liệu: {file.key}
        </DialogDescription>

        <div className="flex items-center justify-between py-4">
          <div className="flex items-center gap-2">
            {enabled ? (
              <Globe className="text-green-500" size={20} />
            ) : (
              <Lock className="text-gray-500" size={20} />
            )}
            <Label>{enabled ? 'Công khai' : 'Riêng tư'}</Label>
          </div>
          <ToggleButton enabled={enabled} onChange={() => setEnabled((prev) => !prev)} />
        </div>

        {file.isFolder && (
          <div className="flex items-start gap-2 rounded-md bg-muted/50 p-3">
            <Checkbox
              id="cascade-privacy"
              checked={cascade}
              onCheckedChange={(checked) => setCascade(!!checked)}
            />
            <Label htmlFor="cascade-privacy" className="font-normal leading-snug cursor-pointer">
              Áp dụng cho toàn bộ file/thư mục con
              <span className="block text-xs text-muted-foreground mt-1">
                Xoá cài đặt công khai/riêng tư riêng đã set trước đó cho các file/thư mục con, để
                chúng kế thừa theo trạng thái mới của thư mục này.
              </span>
            </Label>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Hủy
          </Button>
          <Button onClick={handleSave}>Lưu thay đổi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
